import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {RouteComponentProps, Link} from "react-router-dom";
import {TextField} from "components/common/inputFunctions";
import {
    EGoldField,
    HeaderTitle,
    initialValidationStatus,
    renderProceed,
    roundDown, roundWithUpperValue,
    validateSellGold,
} from "egold/utils";
import {imageBaseUrl} from "config";
import {IEgoldUserDetails, IGoldBalance, ISellPrice, IValidation, IVerifySellPrice} from "StateInterface";
import Timer from "egold/components/timer";
import EGoldHeaderNav from "egold/components/header";
import {indexPath, routePaths} from "egold/routes";
import {clearEligibleSellAmount, getSellPrice} from "egold/actions";
import {IReduxState} from "reducers";
import {connect} from "react-redux";
import KycValidation from "egold/kyc";
import {Snackbar} from "web-com";
import {EVENT_NAMES} from "Dashboard/utils";


interface ISellGoldInput {
    inRupees: string;
    inGrams: string;
}
const initialUserInput = {
    inRupees: "",
    inGrams: "",
};
interface IConnectProps {
    dispatch?: (action: any) => void;
    userDetails: IEgoldUserDetails;
    sellPrice: ISellPrice;
    lockerBalance: IGoldBalance;
    eGoldUserDetails: IEgoldUserDetails;
    verifySellPrice: IVerifySellPrice;
    isAndroid: boolean;
    eligibleSellAmount: number;
}
const minAmountForKyc = 100;

function SellGold(props: IConnectProps & RouteComponentProps) {
    const [userInput, updateUserInput] = useState<ISellGoldInput>(initialUserInput);
    const timerRef: any = useRef();
    const [validationStatus, updateValidationStatus] = useState<IValidation>(initialValidationStatus);
    const [kycRequired, setKycDialog] = useState<boolean>(false);
    const [showSnackBar, setShowSnackBar] = useState(false);
    const {current_price} = props.sellPrice;
    const isGoldBalanceAvailable = props.lockerBalance && Number(props.lockerBalance.gold_balance) > 0;
    useEffect(() => {
       props.dispatch(getSellPrice());
       if(props.eligibleSellAmount !== null) {
            setShowSnackBar(true)
        }
    }, []);
    function handleChange(fieldName: EGoldField, value: number | string) {
        if (fieldName === EGoldField.RUPEES) {
            const gramValue = (Number(value) / current_price);
            updateUserInput((prevState: any) => {
                return{
                    ...prevState,
                    inRupees: value,
                    inGrams: roundDown(gramValue, 4),
                };
            });
        } else if (fieldName === EGoldField.GRAMS) {
            const rupeesValue = roundWithUpperValue((Number(value) * current_price), 2);
            updateUserInput((prevState: any) => {
                return{
                    ...prevState,
                    inGrams: value,
                    inRupees: rupeesValue,
                };
            });
        }
    }
    function handleProceed() {
        const {inRupees, inGrams} = userInput;
        const validation = validateSellGold(inRupees, inGrams, props.lockerBalance);
        if (validation.status) {
            if (props.userDetails) {
                if ((!props.userDetails.panDetails || (props.userDetails.panDetails && Object.entries(props.userDetails.panDetails).length === 0)) && Number(inRupees) >= minAmountForKyc) {
                    setKycDialog(true);
                } else {
                    props.history.push({
                        pathname: `${indexPath}${routePaths.CONFIRM_SELL_PAYMENT}`,
                        state: {userInput},
                    });
                }
            }
            
        }
        updateValidationStatus(validation);
    }
    function renderTextFields() {
        return(
            <div>
            <div className="text-field-container d-inline-flex my-2 mx-2">
                <div className="position-relative">
                    <TextField label="Sell in Rupees"
                               value={userInput.inRupees}
                               fieldName={EGoldField.RUPEES}
                               onChange={handleChange}
                               type="number"
                               error={validationStatus.type === EGoldField.RUPEES ? validationStatus.msg : ""}
                    />
                    <span className="left-text">Min 1</span>
                </div>
                <div className="equal-sign"> = </div>
                <div className="position-relative">
                    <TextField label="Sell in Grams"
                               value={userInput.inGrams}
                               fieldName={EGoldField.GRAMS}
                               onChange={handleChange}
                               type="number"
                               error={validationStatus.type === EGoldField.GRAMS ? validationStatus.msg : ""}
                    />
                    <span className="right-text">Max 30gm(s)</span>
                </div>
            </div>
                {Number(userInput.inRupees) > 0 &&
                <p className="text-center text-dark pt-3"><small>You are selling Rs. {userInput.inRupees} of gold.
                    You will receive Rs. {userInput.inRupees} into your Bank Account.</small></p>
                }
            </div>
        );
    }
    function restartTimer(time) {
        timerRef.current.reStartTimer(time);
    }
    function renderTimer() {
        return(
            <div className="text-center" style={{fontSize: "14px", color: "#000000"}}>
                Price valid for&nbsp;
                <Timer seconds={300}
                       ref={timerRef}
                       handleTimeEnd={() => restartTimer(300)}
                />
            </div>
        );
    }
    const renderKycSecurtyText = () => ( 
        <div className="kyc-security-text">
        {`* For security reasons, to sell gold beyond ₹${minAmountForKyc}, we will be verifying your Pan card and your bank details by penny drop.`}
        </div>
    )
    const onKycClose = () => {
        setKycDialog(false);
    };
    const snackBarCloseHandler = (show) => {
        setShowSnackBar(show);
        props.dispatch(clearEligibleSellAmount());
    }
    return (
        <div className="add-margin">
            <Snackbar 
            show={showSnackBar} 
            type="DANGER" 
            onCloseHandler={(show) => snackBarCloseHandler(show)}
            timeout={12000}
            >
                <div>
                    {`You can sell upto ₹${props.eligibleSellAmount} worth of gold today, as a security measure sell transaction cannot be initiated within 2 days from  date of purchase`}
                </div>
            </Snackbar>
            <EGoldHeaderNav title={HeaderTitle.SELL}/>
            {
                    kycRequired &&
                    <KycValidation
                        active={kycRequired}
                        handleClose={onKycClose}
                        handleProceed={handleProceed}
                    />
            }
            <div className="mt-4">
                {renderGoldBrand(current_price, props.lockerBalance, isGoldBalanceAvailable)}
                {renderTextFields()}
                {renderKycSecurtyText()}
                {renderProceed("Proceed to Sell", handleProceed, !isGoldBalanceAvailable)}
                {renderTimer()}
                {renderTnc()}
            </div>
        </div>
    );
}

function renderGoldBrand(currentPrice: number, lockerBalance: IGoldBalance, isGoldBalanceAvailable: boolean) {
    return(
        <div>
            <div className={"sellGold-brand-container mt-4 mx-2"}>
                <div className="title px-3 py-1">Gold Locker Balance</div>
                {
                    isGoldBalanceAvailable ?
                    <div className="d-flex justify-content-between align-items-center px-3 py-2">
                        <div className="left">
                            <p className="right-text p-0 m-0">24k 99.5% Purity</p>
                            <img className="my-2" src={`${imageBaseUrl}/img/icons/eGold/safegold_logo.svg`}
                                 style={{width: "100px"}}/>
                            <br/>
                            <b className="left-text">{lockerBalance.gold_balance}gm</b>
                        </div>
                        <div className="right">
                            <i className="fa fa-check-circle float-right" style={{color: "green"}} aria-hidden="true"/>
                            <br/>
                            <b className="left-text text-dark">
                                {/*<i className="fa fa-inr price-icon" aria-hidden="true"/>*/}
                                {/*{`000`}*/}
                            </b>
                        </div>
                    </div> : <div className="py-2 px-3" style={{fontSize: "12px"}}>Insufficient gold balance</div>
                }
            </div>
            <div className="bottom-sellGold-text"><b>SafeGold</b> Live Sell Price <b>{currentPrice}/g</b></div>
        </div>
    );
}

function renderTnc() {
    return(
        <div className="tnc-section d-inline-flex justify-content-center mt-5 w-100">
            <span className="text-dark tnc-text">By continuing you agree to the
                <Link to = {`${indexPath}${routePaths.TERMS_AND_CONDITIONS}`}><b> Terms & Conditions</b></Link>
            </span>
        </div>
    );
}

const mapStateToProps = (state: IReduxState): IConnectProps => {
    return {
        sellPrice: state.eGold.sellPrice,
        lockerBalance: state.eGold.goldBalance,
        userDetails: state.eGold.eGoldUserDetails,
        eGoldUserDetails: state.eGold.eGoldUserDetails,
        verifySellPrice: state.eGold.verifySellPrice,
        eligibleSellAmount: state.eGold.eligibleSellAmount,
        isAndroid: state.login.isAndroid
    };
};

export default connect<IConnectProps, {}, {}>(mapStateToProps)(SellGold);
